import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Scale } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type TaskView, ratioLabel } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { PlanGateDialog } from './plan-gate-dialog';

/**
 * Tỷ lệ đã chọn: âm ⇒ `−25%`, dương ⇒ số lần `×1,25` — cùng nhãn với thanh trượt
 * (thang −75% … ×20, 2026-11-02).
 */
function signedPct(value: number): string {
  return ratioLabel(value);
}

/**
 * Khối "Ước lượng" — popover ô SP trên board / Gantt / danh sách và drawer chi tiết
 * việc dùng chung. Người dùng yêu cầu 2026-11-02: "1. Số SP ước lượng bằng bao nhiêu.
 * 2. Thời gian ước lượng bằng bao nhiêu. Ước lượng SP bằng cách nào và bằng task nào,
 * task đó có bao nhiêu sp và chênh lệch bao nhiêu %. Còn các mô tả khác bỏ hết".
 *
 * Drawer từng có bản dài (dòng "Nguồn: …", câu "Chưa ước lượng theo việc mẫu", gợi ý
 * điền SP, bảng so sánh) — người dùng chê "nói quá lộn xộn" (2026-11-02), nên cả hai
 * chỗ chỉ còn bảng nhãn–giá trị này. Không quy giờ ra SP bằng neo của tổ chức: neo
 * chỉ là **định nghĩa** "1 SP to cỡ nào" (APM-SPA), không phải công thức đổi.
 *
 * ⚠️ Không nhận asOf: thuần trình bày `TaskView` người gọi truyền vào.
 */
export function TaskEstimateSummary({
  task,
  canEdit = false,
  onEstimate,
}: {
  readonly task: TaskView;
  readonly canEdit?: boolean;
  readonly onEstimate: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const reference = task.estimateReference ?? null;
  const compared = reference !== null && !reference.manual;
  const source = estimateSourceOf(task);
  const none = t('actualTime.summary.none');
  const method = compared
    ? t(
        source === 'editedAfter'
          ? 'actualTime.summary.methodEdited'
          : 'actualTime.summary.methodReference',
      )
    : t(
        source === 'manualDialog'
          ? 'actualTime.summary.methodDialog'
          : source === 'manualForm'
            ? 'actualTime.summary.methodForm'
            : 'actualTime.summary.methodNone',
      );
  const ratio = reference?.ratioPct ?? 0;

  return (
    <div className="flex flex-col gap-2" data-testid="task-estimate-summary">
      <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-muted-foreground">{t('actualTime.summary.sp')}</dt>
        <dd className="text-sm font-semibold tabular-nums" data-testid="summary-sp">
          {task.storyPoints === null ? none : t('actualTime.spValue', { value: task.storyPoints })}
        </dd>
        <dt className="text-muted-foreground">{t('actualTime.summary.hours')}</dt>
        <dd className="text-sm font-semibold tabular-nums" data-testid="summary-hours">
          {task.estimateHours === null
            ? none
            : t('actualTime.hoursValue', { count: task.estimateHours })}
        </dd>
        <dt className="text-muted-foreground">{t('actualTime.summary.method')}</dt>
        <dd className="font-medium" data-testid="summary-method">
          {method}
        </dd>
        {compared ? (
          <>
            <dt className="text-muted-foreground">{t('actualTime.summary.refTask')}</dt>
            <dd data-testid="summary-ref-task">
              {reference.referenceTaskId ? (
                <a
                  href={`/projects?projectId=${reference.referenceProjectId}&tab=tasks&taskId=${reference.referenceTaskId}`}
                  target="_blank"
                  rel="noreferrer"
                  title={t('actualTime.openReference')}
                  className="inline-flex items-start gap-1 hover:underline"
                >
                  <span className="shrink-0 font-mono text-muted-foreground">
                    {reference.referenceKey}
                  </span>
                  <span className="min-w-0 break-words">{reference.referenceTitle}</span>
                  <ExternalLink aria-hidden className="mt-0.5 size-3 shrink-0" />
                </a>
              ) : (
                t('actualTime.hiddenReference')
              )}
            </dd>
            <dt className="text-muted-foreground">{t('actualTime.summary.refPoints')}</dt>
            <dd className="tabular-nums" data-testid="summary-ref-sp">
              {reference.referenceStoryPoints == null
                ? none
                : t('actualTime.spValue', { value: reference.referenceStoryPoints })}
            </dd>
            <dt className="text-muted-foreground">{t('actualTime.summary.ratio')}</dt>
            <dd
              className={cn('tabular-nums', reference.needsReview && 'font-medium text-rag-amber')}
              data-testid="summary-ratio"
            >
              {t(
                ratio > 0
                  ? 'actualTime.summary.ratioMore'
                  : ratio < 0
                    ? 'actualTime.summary.ratioLess'
                    : 'actualTime.summary.ratioSame',
                { pct: signedPct(ratio) },
              )}
            </dd>
          </>
        ) : null}
      </dl>
      {canEdit && task.kind !== 'milestone' ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="self-start"
          onClick={onEstimate}
        >
          <Scale aria-hidden />
          {t(task.storyPoints === null ? 'actualTime.estimateRun' : 'actualTime.estimateRerun')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Hộp ước lượng (`PlanGateDialog` chế độ `estimate`) cho một việc, kèm làm mới mọi
 * màn có thể đang hiện việc ấy: drawer, Việc của tôi, board, Gantt, danh sách.
 */
export function TaskEstimateDialog({
  task,
  onClose,
}: {
  readonly task: TaskView;
  readonly onClose: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  return (
    <PlanGateDialog
      mode="estimate"
      task={{
        id: task.id,
        title: task.title,
        missing: ['estimateHours'],
        ownerName: task.assigneeName,
        estimateHours: task.estimateHours,
        storyPoints: task.storyPoints,
      }}
      onCancel={onClose}
      onDone={() => {
        onClose();
        // Drawer đọc `['task', id]` của riêng nó — xem `MoveButton` (APM-TASK-j2);
        // board / Gantt / danh sách đọc khoá của riêng chúng.
        void queryClient.invalidateQueries({ queryKey: ['task', task.id] });
        void queryClient.invalidateQueries({ queryKey: ['me', 'tasks'], exact: false });
        void queryClient.invalidateQueries({ queryKey: ['gantt'], exact: false });
        void queryClient.invalidateQueries({ queryKey: ['board'], exact: false });
        // Cột Backlog của board có khoá riêng (`boardKeys.backlog`).
        void queryClient.invalidateQueries({ queryKey: ['backlog'], exact: false });
        void queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false });
      }}
    />
  );
}

/**
 * SP này đến từ đâu — người dùng yêu cầu 2026-11-02 ("show rõ là họ tự điền hay
 * estimate qua một task cũ").
 *
 * - `reference`: lần ước lượng gần nhất so với một việc cũ, và SP chưa bị sửa sau đó.
 * - `editedAfter`: có so với việc cũ, nhưng SP hiện tại khác số lúc ấy ⇒ đã sửa tay.
 * - `manualDialog`: "Tự điền số SP" trong hộp ước lượng (không so với việc nào).
 * - `manualForm`: có SP mà chưa từng qua hộp ước lượng ⇒ điền ở form việc (hoặc đồng bộ).
 * - `none`: chưa có SP.
 */
export type EstimateSource = 'reference' | 'editedAfter' | 'manualDialog' | 'manualForm' | 'none';

export function estimateSourceOf(
  task: Pick<TaskView, 'storyPoints' | 'estimateReference'>,
): EstimateSource {
  const reference = task.estimateReference ?? null;
  const sp = task.storyPoints;
  if (reference !== null && !reference.manual) {
    const changed = reference.storyPoints != null && sp !== null && sp !== reference.storyPoints;
    if (changed) return 'editedAfter';
    return sp === null ? 'none' : 'reference';
  }
  if (sp === null) return 'none';
  if (reference !== null && reference.manual) {
    return reference.storyPoints == null || reference.storyPoints === sp
      ? 'manualDialog'
      : 'manualForm';
  }
  return 'manualForm';
}
